import { useAtomValue } from 'jotai';
import { viewTitleBarAtom } from '../viewTitleBarAtom';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ViewName } from '@view-model/domain/view';
import type { ViewId, ViewModelId } from 'balus-common';
import type { DragContext } from '@model-framework/ui';
import type { ThemeColor } from '@view-model/models/common/color';
import { ReadonlyTitleBar } from './ReadonlyTitleBar';
import { DraggableTitleBar } from './DraggableTitleBar';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';

type Props = {
  readonly: boolean;
  viewIsLocked: boolean;
  viewIsPositionFixed: boolean;
  hasMeaningPanel: boolean;
  viewRect: Rect;
  viewModelId: ViewModelId;
  name: ViewName;
  viewId: ViewId;
  onClick(e: React.MouseEvent<SVGGElement>): void;
  onCopyView(): void;
  onOpenPopupMenu(): void;
  onDragStart(viewId: ViewId): void;
  onDrag(context: DragContext): void;
  onDragEnd(): void;
  onAddMeaningPanel(): void;
  onToggleMeaningPanel(): void;
  onToggleShowNodeCreatedUser(): void;
  viewThemeColor: ThemeColor;
  getGuardProps: GetGuardProps;
  shouldSuppressDrag?: (e: MouseEvent) => boolean;
};

export const ViewTitleBar: React.FC<Props> = ({
  viewModelId,
  readonly,
  viewIsLocked,
  viewIsPositionFixed,
  hasMeaningPanel,
  viewRect,
  name,
  viewId,
  onClick,
  onCopyView,
  onOpenPopupMenu,
  onDragStart,
  onDrag,
  onDragEnd,
  onAddMeaningPanel,
  onToggleMeaningPanel,
  onToggleShowNodeCreatedUser,
  viewThemeColor,
  getGuardProps,
  shouldSuppressDrag,
}: Props) => {
  const { height, shouldHideMenuButtons, menu, margin } = useAtomValue(viewTitleBarAtom);

  if (readonly || viewIsPositionFixed) {
    return (
      <ReadonlyTitleBar
        viewRect={viewRect}
        name={name}
        viewId={viewId}
        isLocked={viewIsLocked}
        onCopyView={onCopyView}
        onOpenPopupMenu={onOpenPopupMenu}
        onAddMeaningPanel={onAddMeaningPanel}
        onToggleMeaningPanel={onToggleMeaningPanel}
        onToggleShowNodeCreatedUser={onToggleShowNodeCreatedUser}
        width={viewRect.width}
        height={height}
        margin={margin}
        menuContainerWidth={menu.containerWidth}
        shouldHideMenuButtons={shouldHideMenuButtons}
        viewThemeColor={viewThemeColor}
        readonly={readonly}
        hasMeaningPanel={hasMeaningPanel}
        getGuardProps={getGuardProps}
      />
    );
  }

  return (
    <DraggableTitleBar
      viewRect={viewRect}
      name={name}
      viewId={viewId}
      viewModelId={viewModelId}
      isLocked={viewIsLocked}
      hasMeaningPanel={hasMeaningPanel}
      onClick={onClick}
      onCopyView={onCopyView}
      onOpenPopupMenu={onOpenPopupMenu}
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragEnd={onDragEnd}
      onAddMeaningPanel={onAddMeaningPanel}
      onToggleMeaningPanel={onToggleMeaningPanel}
      onToggleShowNodeCreatedUser={onToggleShowNodeCreatedUser}
      width={viewRect.width}
      height={height}
      margin={margin}
      menuContainerWidth={menu.containerWidth}
      shouldHideMenuButtons={shouldHideMenuButtons}
      viewThemeColor={viewThemeColor}
      getGuardProps={getGuardProps}
      shouldSuppressDrag={shouldSuppressDrag}
    />
  );
};
